
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from "recharts";
import { UserCheck, UserX, TrendingUp } from "lucide-react";
import { formatCountWithArabicNumbers, formatPercentageWithArabicNumbers } from "@/utils/arabicNumbers";

interface AttendanceChartsSectionProps {
  filteredStudents: any[];
}

export const AttendanceChartsSection = ({
  filteredStudents,
}: AttendanceChartsSectionProps) => {
  
  // console.log('filteredStudents in AttendanceChartsSection: ', filteredStudents);
  // إعداد بيانات الحضور والغياب
  const attendanceData = () => {
    const presentStudents = filteredStudents.filter(s => (s.attendancePercentage || 0) >= 90).length;
    const absentStudents = filteredStudents.filter(s => (s.absenceDays || 0) > 2).length;
    const totalStudents = filteredStudents.length;

    return [
      {
        category: "حضور منتظم",
        count: presentStudents,
        percentage: totalStudents > 0 ? Math.round((presentStudents / totalStudents) * 100) : 0
      },
      {
        category: "غياب",
        count: totalStudents - presentStudents,
        percentage: totalStudents > 0 ? Math.round(((totalStudents - presentStudents) / totalStudents) * 100) : 0
      },
      {
        category: "غياب متكرر",
        count: absentStudents,
        percentage: totalStudents > 0 ? Math.round((absentStudents / totalStudents) * 100) : 0
      }
    ];
  };

  const chartData = attendanceData();
  const totalStudents = filteredStudents.length;

  const chartConfig = {
    count: {
      label: "عدد الطلاب",
      color: "hsl(var(--chart-1))",
    },
  };



  return (
    <div className="mb-6" dir="ltr">
      {/* بطاقة مدمجة للرسم البياني والإحصائيات */}
      <Card className="bg-gradient-to-br from-white/95 to-white/80 backdrop-blur-lg shadow-xl lg:shadow-2xl border-0 rounded-2xl lg:rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-600/10"></div>
        <CardHeader className="relative z-10 pb-3 p-4 lg:p-6 bg-secondary text-white rounded-t-2xl lg:rounded-t-3xl">
          <CardTitle className="flex items-center gap-2 text-lg lg:text-xl font-bold text-white">
            <TrendingUp className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
            الرسم البياني للحضور والغياب
          </CardTitle>
        </CardHeader>
        <CardContent className="relative z-10 p-4 lg:p-6 pt-4">
          <div className="flex flex-col gap-6">
            {/* الإحصائيات التفصيلية في الأعلى */}
            <div className="w-full">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                <div className="flex items-center justify-start gap-4 p-3 lg:p-4 bg-blue-50/80 backdrop-blur-sm rounded-xl border border-blue-200/50 shadow-sm">
                  <TrendingUp className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-blue-700 flex-shrink-0">إجمالي الطلاب</span>
                  <div className="text-right ml-auto pl-4">
                    <div className="text-xl font-bold text-blue-600">{formatCountWithArabicNumbers(totalStudents)}</div>
                  </div>
                </div>

                <div className="flex items-center justify-start gap-4 p-3 lg:p-4 bg-green-50/80 backdrop-blur-sm rounded-xl border border-green-200/50 shadow-sm">
                  <UserCheck className="h-4 w-4 text-green-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-green-700 flex-shrink-0">حضور منتظم</span>
                  <div className="text-right ml-auto pl-4">
                    <div className="text-xl font-bold text-green-600">{formatCountWithArabicNumbers(chartData[0]?.count || 0)}</div>
                    <div className="text-sm text-green-600">{formatPercentageWithArabicNumbers(chartData[0]?.percentage || 0)}</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-start gap-4 p-3 lg:p-4 bg-gray-50/80 backdrop-blur-sm rounded-xl border border-gray-200/50 shadow-sm">
                  <UserX className="h-4 w-4 text-gray-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-700 flex-shrink-0">غياب</span>
                  <div className="text-right ml-auto pl-4">
                    <div className="text-xl font-bold text-gray-600">{formatCountWithArabicNumbers(chartData[1]?.count || 0)}</div>
                    <div className="text-sm text-gray-600">{formatPercentageWithArabicNumbers(chartData[1]?.percentage || 0)}</div>
                  </div>
                </div>

                <div className="flex items-center justify-start gap-4 p-3 lg:p-4 bg-red-50/80 backdrop-blur-sm rounded-xl border border-red-200/50 shadow-sm">
                  <UserX className="h-4 w-4 text-red-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-red-700 flex-shrink-0">غياب متكرر</span>
                  <div className="text-right ml-auto pl-4">
                    <div className="text-xl font-bold text-red-600">{formatCountWithArabicNumbers(chartData[2]?.count || 0)}</div>
                    <div className="text-sm text-red-600">{formatPercentageWithArabicNumbers(chartData[2]?.percentage || 0)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* الرسم البياني في الأسفل */}
            <div className="bg-gradient-to-br from-slate-50/50 to-blue-50/30 p-8 rounded-3xl border border-slate-200/50 shadow-inner">
              <ChartContainer config={chartConfig}>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart 
                    data={chartData} 
                    margin={{ top: 30, right: 40, left: 60, bottom: 5 }} 
                    barCategoryGap="25%"
                  >
                    {/* شبكة الخلفية */}
                    <CartesianGrid 
                      strokeDasharray="5 5" 
                      stroke="#cbd5e1" 
                      strokeWidth={1}
                      horizontal={true}
                      vertical={false}
                    />
                    
                    {/* محور السينات (X-Axis) - الفئات */}
                    <XAxis 
                      dataKey="category" 
                      axisLine={{ stroke: '#475569', strokeWidth: 2 }}
                      tickLine={{ stroke: '#475569', strokeWidth: 2 }}
                      tick={{ 
                        fontSize: 14, 
                        fill: '#1e293b', 
                        fontWeight: 600,
                        textAnchor: 'middle'
                      }}
                      interval={0}
                      height={60}
                      dy={10}
                    />
                    
                    {/* محور الصادات (Y-Axis) - عدد الطلاب */}
                    <YAxis 
                      axisLine={{ stroke: '#475569', strokeWidth: 2 }}
                      tickLine={{ stroke: '#475569', strokeWidth: 2 }}
                      tick={{ 
                        fontSize: 12, 
                        fill: '#1e293b', 
                        fontWeight: 600 
                      }}
                      width={50}
                      label={{ 
                        value: 'عدد الطلاب', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { 
                          textAnchor: 'middle', 
                          fill: '#1e293b', 
                          fontSize: '16px', 
                          fontWeight: '700'
                        }
                      }}
                    />
                    <ChartTooltip 
                      content={
                        <ChartTooltipContent 
                          contentStyle={{
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            border: '1px solid #e0e7ff',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                          }}
                          formatter={(value, name) => [
                            `${value} طالب (${chartData.find(d => d.count === value)?.percentage || 0}%)`,
                            name
                          ]}
                        />
                      }
                    />
                    <Bar 
                      dataKey="count" 
                      radius={[8, 8, 0, 0]}
                      strokeWidth={2}
                    >
                      <Cell fill="#10b981" stroke="#059669" strokeWidth={2} />
                      <Cell fill="#6b7280" stroke="#4b5563" strokeWidth={2} />
                      <Cell fill="#ef4444" stroke="#dc2626" strokeWidth={2} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartContainer>
              
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
